import logoImage from '@/assets/images/logo.png';
import { useState, useEffect } from 'react';

const navLinks = [
  { label: 'Home', href: '#home' },
  { label: 'Sobre', href: '#about' },
  { label: 'Ciclo', href: '#cycle' },
  { label: 'Serviços', href: '#services' },
  { label: 'Soluções', href: '#solutions' },
  { label: 'IA', href: '#ai' },
  { label: 'Contratos', href: '#pricing' },
  { label: 'Contato', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      const sections = navLinks.map((l) => l.href.replace('#', ''));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    const id = href.replace('#', '');
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-b border-zinc-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => handleNavClick(e, '#home')}
            className="flex items-center gap-2.5 cursor-pointer"
          >
            <img
              src={logoImage}
              alt="Almadata Logo"
              className="h-9 w-auto"
            />
            <span
              className={`font-bold text-lg font-sora tracking-tight transition-colors ${
                scrolled ? 'text-zinc-900' : 'text-white'
              }`}
            >
              alma<span className="text-emerald-500">data</span>
            </span>
          </a>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => {
              const sectionId = link.href.replace('#', '');
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => handleNavClick(e, link.href)}
                  className={`text-sm font-medium font-inter transition-colors cursor-pointer whitespace-nowrap relative group ${
                    scrolled ? 'text-zinc-700' : 'text-white/90'
                  } ${isActive ? 'text-emerald-500' : ''} hover:text-emerald-500`}
                >
                  {link.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                  />
                </a>
              );
            })}
            <a
              href="#contact"
              onClick={(e) => handleNavClick(e, '#contact')}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold font-sora transition-all cursor-pointer whitespace-nowrap"
            >
              Fale Conosco
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg cursor-pointer transition-colors ${
              scrolled ? 'text-zinc-900 hover:bg-zinc-100' : 'text-white hover:bg-white/10'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-xl`} />
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-300 ${
            mobileOpen ? 'max-h-[32rem] opacity-100' : 'max-h-0 opacity-0'
          } bg-white border-t border-zinc-100`}
        >
          <div className="py-3">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => handleNavClick(e, link.href)}
                className="block py-3 px-5 text-zinc-700 hover:text-emerald-500 hover:bg-emerald-50 text-sm font-medium font-inter cursor-pointer transition-colors"
              >
                {link.label}
              </a>
            ))}
            <div className="px-5 py-3">
              <a
                href="#contact"
                onClick={(e) => handleNavClick(e, '#contact')}
                className="block bg-emerald-500 hover:bg-emerald-600 text-white text-center py-3 rounded-full text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap"
              >
                Fale Conosco
              </a>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
